/** В API у контрактов section иногда пустой — считаем contracts */
export const normalizeEntitySection = (section) => {
  const value = String(section || "").toLowerCase();
  return value || "contracts";
};

export const normalizeLinkedEntities = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item && typeof item === "object")
    .map((item) => ({
      id: item.id ?? "",
      name: item.name ?? item.title ?? "",
      section: normalizeEntitySection(item.section),
      items: normalizeLinkedEntities(item.linked_entities || item.items),
    }));
};

/** Листовые сущности из дерева linked_entities; у каждой есть section: contracts | complexes | addresses */
export const flattenLinkedEntities = (entities) => {
  const result = [];
  const walk = (list) => {
    if (!Array.isArray(list)) return;
    list.forEach((item) => {
      if (!item?.name) return;
      const nested = item.items;
      if (Array.isArray(nested) && nested.length > 0) {
        walk(nested);
      } else {
        result.push({
          id: item.id,
          name: String(item.name).trim(),
          section: normalizeEntitySection(item.section),
        });
      }
    });
  };
  walk(entities);
  return result;
};

const dedupeEntities = (items) => {
  const seen = new Set();
  return items.filter((e) => {
    const key = `${e.section}:${e.id}:${e.name}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Перечень для таблицы — только то, что пришло в linked_entities (с учётом вложенных items) */
export const getLinkedEntityDisplayItems = (doc) =>
  dedupeEntities(flattenLinkedEntities(doc?.linked_entities));

const LINKED_ENTITY_SECTION_ORDER = ["complexes", "addresses", "contracts"];

const LINKED_ENTITY_SECTION_LABELS = {
  complexes: "Комплексы",
  addresses: "Адреса",
  contracts: "Контракты",
};

/** Группы для ячейки, когда в items вперемешку complexes и addresses */
export const groupLinkedEntitiesBySection = (doc) => {
  const items = getLinkedEntityDisplayItems(doc);
  const buckets = new Map();

  items.forEach((item) => {
    const section = normalizeEntitySection(item.section);
    if (!buckets.has(section)) buckets.set(section, []);
    buckets.get(section).push(item);
  });

  const ordered = [
    ...LINKED_ENTITY_SECTION_ORDER.filter((section) => buckets.has(section)),
    ...[...buckets.keys()].filter((section) => !LINKED_ENTITY_SECTION_ORDER.includes(section)),
  ];

  return ordered.map((section) => ({
    section,
    label: LINKED_ENTITY_SECTION_LABELS[section] || section,
    items: buckets.get(section),
  }));
};

export const hasMixedLinkedEntitySections = (doc) => {
  const sections = new Set(
    getLinkedEntityDisplayItems(doc).map((e) => normalizeEntitySection(e.section))
  );
  return sections.size > 1;
};

export const getLinkedEntityPath = (entity) => {
  if (!entity) return null;

  if (entity.section === "complexes") {
    if (entity.id) {
      return `/complex?target_id=${encodeURIComponent(entity.id)}&open=1`;
    }
    if (entity.name) {
      return `/complex?complex_serial=${encodeURIComponent(entity.name)}&open=1`;
    }
    return null;
  }
  if (entity.section === "addresses") {
    if (entity.id) {
      return `/addresses?target_id=${encodeURIComponent(entity.id)}&open=1`;
    }
    return null;
  }
  return null;
};

export const getLinkedEntitiesColumnTitle = (items) => {
  const list = (Array.isArray(items) ? items : []).map((e) => ({
    ...e,
    section: normalizeEntitySection(e.section),
  }));

  const hasContracts = list.some((e) => e.section === "contracts");
  const hasComplexes = list.some((e) => e.section === "complexes");
  const hasAddresses = list.some((e) => e.section === "addresses");

  const titleParts = [];
  if (hasContracts) titleParts.push("контрактов");
  if (hasComplexes) titleParts.push("комплексов");
  if (hasAddresses) titleParts.push("адресов");

  if (titleParts.length === 0) return "Перечень";
  if (titleParts.length === 1) {
    return `Перечень ${titleParts[0]}`;
  }
  return `Перечень ${titleParts.join("/")}`;
};

export const normalizeDocument = (item) => {
  const src = item && typeof item === "object" ? item : {};
  const documentTypeId =
    src.document_type_id ??
    src.type_id ??
    src.document_type?.id ??
    src.type?.id ??
    "";
  return {
    id: src.id ?? "",
    original_name: src.original_name ?? "",
    url: src.url ?? "",
    size: Number.isFinite(Number(src.size)) ? Number(src.size) : 0,
    format: src.format ?? "",
    section: src.section ?? "",
    type_name: src.type_name ?? "",
    document_type_id: documentTypeId,
    linked_entities: normalizeLinkedEntities(src.linked_entities),
    created: src.created ?? null,
  };
};
